import React, { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown } from 'lucide-react';
import { breedingValueGroups } from '@/lib/breedingValueTraits';

export interface BreedingValueFilter {
  value: string;
  variance: string;
}

export interface AppliedFilters {
  priceMin: string;
  priceMax: string;
  breedingValues: Record<string, BreedingValueFilter>;
}

interface BreedingValueFiltersProps {
  onApply: (filters: AppliedFilters) => void;
  onClear: () => void;
  appliedFilters: AppliedFilters;
}

const emptyFilters: AppliedFilters = {
  priceMin: '',
  priceMax: '',
  breedingValues: {},
};

const BreedingValueFilters: React.FC<BreedingValueFiltersProps> = ({
  onApply,
  onClear,
  appliedFilters,
}) => {
  const [draft, setDraft] = useState<AppliedFilters>(appliedFilters);
  const [isOpen, setIsOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const isDirty = JSON.stringify(draft) !== JSON.stringify(appliedFilters);

  const updateBV = useCallback((key: string, field: 'value' | 'variance', val: string) => {
    setDraft(prev => ({
      ...prev,
      breedingValues: {
        ...prev.breedingValues,
        [key]: {
          value: field === 'value' ? val : (prev.breedingValues[key]?.value || ''),
          variance: field === 'variance' ? val : (prev.breedingValues[key]?.variance || ''),
        },
      },
    }));
  }, []);

  const handleApply = () => {
    // Clean out empty entries
    const cleanBV: Record<string, BreedingValueFilter> = {};
    for (const [k, v] of Object.entries(draft.breedingValues)) {
      if (v.value) cleanBV[k] = v;
    }
    onApply({ ...draft, breedingValues: cleanBV });
  };

  const handleClear = () => {
    setDraft(emptyFilters);
    onClear();
  };

  const hasAnyFilter = draft.priceMin || draft.priceMax || Object.values(draft.breedingValues).some(v => v.value);

  return (
    <div className="space-y-4">
      {/* Price Filter */}
      <div>
        <label className="block text-sm font-medium mb-2">Price Range</label>
        <div className="flex gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={draft.priceMin}
            onChange={e => setDraft(prev => ({ ...prev, priceMin: e.target.value }))}
            className="h-9 text-sm"
          />
          <Input
            type="number"
            placeholder="Max"
            value={draft.priceMax}
            onChange={e => setDraft(prev => ({ ...prev, priceMax: e.target.value }))}
            className="h-9 text-sm"
          />
        </div>
      </div>

      {/* Breeding Values */}
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-between w-full text-sm font-medium py-2 hover:text-primary transition-colors"
          >
            <span>Breeding Values</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-3 pt-1">
            {breedingValueGroups.map(group => {
              const groupOpen = openGroups[group.label] ?? false;
              return (
                <Collapsible
                  key={group.label}
                  open={groupOpen}
                  onOpenChange={val => setOpenGroups(prev => ({ ...prev, [group.label]: val }))}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center justify-between w-full text-xs font-semibold text-muted-foreground uppercase tracking-wide py-1 hover:text-foreground transition-colors"
                    >
                      <span>{group.label}</span>
                      <ChevronDown className={`w-3 h-3 transition-transform ${groupOpen ? 'rotate-180' : ''}`} />
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="space-y-2 pt-1 pb-2">
                      {/* Column headers */}
                      <div className="grid grid-cols-[3rem_1fr_1fr] gap-1.5 text-[10px] text-muted-foreground uppercase tracking-wide px-0.5">
                        <span></span>
                        <span>Value</span>
                        <span>+/-</span>
                      </div>
                      {group.traits.map(trait => {
                        const bv = draft.breedingValues[trait.key] || { value: '', variance: '' };
                        return (
                          <div key={trait.key} className="grid grid-cols-[3rem_1fr_1fr] gap-1.5 items-center">
                            <span className="text-xs font-medium text-muted-foreground">{trait.abbr}</span>
                            <Input
                              type="number"
                              placeholder="—"
                              value={bv.value}
                              onChange={e => updateBV(trait.key, 'value', e.target.value)}
                              className="h-7 text-xs px-2"
                            />
                            <Input
                              type="number"
                              placeholder="—"
                              value={bv.variance}
                              onChange={e => updateBV(trait.key, 'variance', e.target.value)}
                              className="h-7 text-xs px-2"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Apply / Clear */}
      <div className="flex gap-2">
        <Button
          onClick={handleApply}
          disabled={!isDirty}
          className="flex-1"
          size="sm"
        >
          Apply Filters
        </Button>
        {hasAnyFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClear}
            className="text-xs"
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};

export default BreedingValueFilters;
